// src/utils/mediaTransform.js

/**
 * Gumlet CDN prefix for image optimization.
 * Set REACT_APP_DISABLE_GUMLET=true to bypass Gumlet and serve images directly.
 */
export const GUMLET_PREFIX_URL = "https://wishlink-proxy.gumlet.io/fetch/";
export const GUMLET_ENABLED = process.env.REACT_APP_DISABLE_GUMLET !== "true";

/**
 * Transforms media URLs to use Gumlet CDN for optimization.
 *
 * Why encodeURIComponent?
 * Gumlet expects the origin URL to be passed as a single path segment after `/fetch/`.
 * `encodeURIComponent` safely encodes reserved characters like `: / ? & #` so querystrings
 * don't break the Gumlet fetch path.
 *
 * When Gumlet is disabled, we strip the prefix (if present) and decode it back.
 *
 * @param {string} url - The original media URL (or Gumlet-wrapped URL)
 * @returns {string} - The transformed URL with Gumlet prefix (if enabled), else the origin URL
 *
 * @example
 * transformMedia("https://cdn-alabaster.wishlink.com/image.jpg",  1080)
 * Returns: "https://wishlink-proxy.gumlet.io/fetch/https%3A%2F%2Fcdn-alabaster.wishlink.com%2Fimage.jpg?w=1080"
 */
export function transformMedia(url: string, width?: number): string {
  if (!url) return "";
  // Skip local URLs that shouldn't go through Gumlet:
  if (
    url.startsWith("blob:") ||
    url.startsWith("data:") ||
    url.startsWith("file:") ||
    url.startsWith("/") ||
    url.includes("cdn-cgi") ||
    url.includes("static5.lenskart.com")
  ) {
    return url;
  }

  // If Gumlet is disabled, return the original URL.
  if (!GUMLET_ENABLED) {
    // Strip existing Gumlet prefix if present and decode it back to origin URL.
    if (url.startsWith(GUMLET_PREFIX_URL)) {
      const raw = url.slice(GUMLET_PREFIX_URL.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        // Fallback: if the suffix isn't URI-encoded, return it as-is.
        return raw;
      }
    }
    return url;
  }

  // Already has Gumlet prefix, return as-is.
  if (url.startsWith(GUMLET_PREFIX_URL)) return url;

  const queryString = width ? `?w=${width}` : "";
  // Encode the origin URL so it can safely live inside the Gumlet `/fetch/` path segment.
  return `${GUMLET_PREFIX_URL}${encodeURIComponent(url)}${queryString}`;
}
